import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';

/** Where a project stands, from the bid to the finished building. */
export const PROJECT_STATUSES = [
	'bidding',
	'awarded',
	'in_progress',
	'complete',
	'canceled',
] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

/**
 * A project: the real-world construction project several of the tenant's
 * customers may bid on or work ("Wayne State Dorms"), sales relationships
 * plan section 4 (J3, J4, JQ-3; PD-17094). Behind the `jobs` add-on.
 *
 * Customers take part through their opportunities (the bid) and jobs (the won
 * work), each of which may name the project (`fk_project__id`). The general
 * contractor and the building owner are picked from customers or typed; the
 * typed name is kept when no customer is picked. Bonding and lien-notice
 * dates are left for tenant custom fields (J4).
 *
 * Projects are visible to everyone with `projects:read`; each customer's
 * opportunity and job details on a project follow customer visibility.
 * SM2-only; SM1 never reads it.
 */
@Index('project__status', ['status'], {})
@Index('project__fk_client__id_general_contractor', ['fkClientIdGeneralContractor'], {})
@Index('project__fk_client__id_owner', ['fkClientIdOwner'], {})
@Entity('project')
export class ProjectEntity extends BaseEntity {
	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	@Column('varchar', { name: 'status', length: 16, default: () => "'bidding'" })
	public status!: ProjectStatus;

	@Column('text', { name: 'description', nullable: true })
	public description!: string | null;

	// The job-site address.
	@Column('varchar', { name: 'address1', length: 255, nullable: true })
	public address1!: string | null;

	@Column('varchar', { name: 'city', length: 255, nullable: true })
	public city!: string | null;

	@Column('varchar', { name: 'state', length: 64, nullable: true })
	public state!: string | null;

	@Column('varchar', { name: 'zip', length: 32, nullable: true })
	public zip!: string | null;

	@Column('date', { name: 'bidDate', nullable: true })
	public bidDate!: string | null;

	@Column('int', {
		name: 'fk_client__id_general_contractor',
		unsigned: true,
		nullable: true,
	})
	public fkClientIdGeneralContractor!: number | null;

	/** Typed general contractor, when they are not one of the tenant's customers. */
	@Column('varchar', { name: 'generalContractorName', length: 255, nullable: true })
	public generalContractorName!: string | null;

	@Column('int', { name: 'fk_client__id_owner', unsigned: true, nullable: true })
	public fkClientIdOwner!: number | null;

	/** Typed building owner, when they are not one of the tenant's customers. */
	@Column('varchar', { name: 'ownerName', length: 255, nullable: true })
	public ownerName!: string | null;

	@ManyToOne(() => ClientEntity, { onDelete: 'SET NULL', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_client__id_general_contractor', referencedColumnName: 'id' }])
	public generalContractor!: ClientEntity | null;

	@ManyToOne(() => ClientEntity, { onDelete: 'SET NULL', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_client__id_owner', referencedColumnName: 'id' }])
	public buildingOwner!: ClientEntity | null;
}
